// src/components/properties/FavoritesList.jsx
import { useState, useCallback, useMemo, useEffect } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import PropertiesCard from "./PropertyCard";
import Pagination from "./shared/Pagination";
import { isPropertyRegistered } from "../utils";
import { propertiesAPI } from "./PropertiesAPI";

const ITEMS_PER_PAGE = 9;

/**
 * Displays the user's favorited properties with sort, county filter, and pagination.
 *
 * Property resolution order for each favorite:
 *   1. Match against propertiesData (current filtered list) by propertyId or prop_int_id
 *   2. Match against resolvedProperties (independently fetched via getPropertyDetail)
 *   3. Fall back to the slim favorite record — triggers placeholder card
 */
const FavoritesList = ({
  favorites,
  propertiesData,
  registrations,
  onDetailsClick,
  onRegisterClick,
  onAddFavorite,
  onRemoveFavorite,
  isPropertyFavorite,
  triggerToast,
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [sortOrder] = useState("asc");
  const [selectedCounty, setSelectedCounty] = useState("All");
  // Map of favorite_id → full property object fetched independently
  const [resolvedProperties, setResolvedProperties] = useState({});
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  // Fetch full property details for any favorite not resolvable from propertiesData
  useEffect(() => {
    if (!Array.isArray(favorites) || favorites.length === 0) return;

    const unresolved = favorites.filter((fav) => {
      // Already resolved
      if (resolvedProperties[fav.favorite_id]) return false;
      // Resolvable from propertiesData
      const inData =
        propertiesData?.find((p) => p.propertyId === fav.property_id) ??
        propertiesData?.find((p) => String(p.id) === String(fav.prop_int_id));
      return !inData;
    });

    if (unresolved.length === 0) return;

    const fetchDetails = async () => {
      setIsFetchingDetails(true);
      const results = await Promise.allSettled(
        unresolved.map(async (fav) => {
          try {
            const details = await propertiesAPI.getPropertyDetail({
              id: fav.prop_int_id || fav.id || fav.property_id,
              propertyId: fav.property_id,
              saleId: Number(fav.sale_id) || 0,
              // Always use the raw sale type code, not the description.
              // Fall back to "F" for old records where sale_type may be empty.
              saleType: fav.sale_type || "F",
            });
            return { favorite_id: fav.favorite_id, fav, data: details };
          } catch {
            return { favorite_id: fav.favorite_id, fav, data: null };
          }
        })
      );

      const newResolved = {};
      results.forEach((result) => {
        if (result.status === "fulfilled") {
          const { favorite_id, data, fav } = result.value;
          if (data?.propertyCardDetail) {
            // Full data available — build the complete property shape
            newResolved[favorite_id] = {
              county: fav.county,
              state: fav.state,
              ...data.propertyCardDetail,
              ...data.propertyInfo,
              auctionDetail: data.auctionDetail,
              originalSaleId: data.propertyCardDetail?.originalSaleId ?? 0,
              saleType: fav.sale_type,
              id: Number(data.propertyCardDetail?.id || fav.prop_int_id || fav.id),
            };
          } else {
            // API returned no usable data — mark as unresolvable so placeholder renders
            newResolved[favorite_id] = null;
          }
        } else {
          // Promise rejected — mark as unresolvable so placeholder renders
          newResolved[result.value?.favorite_id] = null;
        }
      });

      setResolvedProperties((prev) => ({ ...prev, ...newResolved }));
      setIsFetchingDetails(false);
    };

    fetchDetails();
  }, [favorites, propertiesData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive unique county options from the favorites array
  const countyOptions = useMemo(() => {
    if (!Array.isArray(favorites)) return [];
    const unique = Array.from(new Set(favorites.filter((f) => f.county).map((f) => `${f.county}, ${f.state}`))).sort();
    return unique;
  }, [favorites]);

  const sorted = useCallback(() => {
    if (!Array.isArray(favorites)) return [];
    return [...favorites]
      .filter((f) => selectedCounty === "All" || `${f.county}, ${f.state}` === selectedCounty)
      .sort((a, b) => {
        const diff = new Date(a.sale_date || a.auctionDate) - new Date(b.sale_date || b.auctionDate);
        return sortOrder === "asc" ? diff : -diff;
      });
  }, [favorites, sortOrder, selectedCounty])();
  // TODO: sortOrder in the above deps array might be redundant

  if (!favorites || favorites.length === 0) {
    return (
      <p className="text-center text-muted mt-3">
        No favorites yet. Click <b>Favorite</b> on any property card to save it here.
      </p>
    );
  }

  const totalPages = Math.ceil(sorted.length / ITEMS_PER_PAGE);
  const paginated = sorted.slice(currentPage * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE);

  return (
    <div className="favorites-container">
      <h3>Favorite Properties</h3>

      {/* County filter */}
      {countyOptions.length > 1 && (
        <div className="d-flex justify-content-center mb-3">
          <Form.Control
            as="select"
            value={selectedCounty}
            onChange={(e) => {
              setSelectedCounty(e.target.value);
              setCurrentPage(0);
            }}
            style={{ maxWidth: "300px" }}
          >
            <option value="All">All Counties</option>
            {countyOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Form.Control>
        </div>
      )}

      {isFetchingDetails && (
        <div className="d-flex justify-content-center align-items-center mb-3 gap-2">
          <Spinner animation="border" size="sm" role="status" />
          <span className="text-muted">Loading property details...</span>
        </div>
      )}

      <div className="d-flex flex-wrap justify-content-center">
        {paginated.map((fav, idx) => {
          // Resolution order:
          // 1. Current propertiesData (matched by propertyId or prop_int_id)
          // 2. Independently fetched via getPropertyDetail
          // 3. Slim favorite record (triggers placeholder)
          const fromPropertiesData =
            propertiesData?.find((p) => p.propertyId === fav.property_id) ??
            propertiesData?.find((p) => String(p.id) === String(fav.prop_int_id));

          const fullProperty = fromPropertiesData ?? resolvedProperties[fav.favorite_id] ?? fav;

          // If not yet resolved from propertiesData and fetch is still in flight,
          // show a loading placeholder rather than rendering a broken card.
          // Use 'in' operator so null entries (failed fetches) are treated as
          // resolved rather than stuck loading forever.
          if (!fromPropertiesData && !(fav.favorite_id in resolvedProperties)) {
            return (
              <div
                key={fav.favorite_id || idx}
                className="d-flex flex-column justify-content-center align-items-center"
                style={{
                  width: "21rem",
                  minHeight: "200px",
                  margin: "2rem",
                  padding: "1rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                }}
              >
                <Spinner animation="border" size="sm" role="status" className="mb-2" />
                <span className="text-muted" style={{ fontSize: "12px" }}>
                  {fav.county}, {fav.state}
                </span>
              </div>
            );
          }

          const saleId =
            fav.sale_type === "F" ? fullProperty.saleId : fullProperty.originalSaleId ?? fullProperty.saleId;
          const isReg = isPropertyRegistered(fullProperty, registrations);

          // If the full property couldn't be resolved to a renderable shape,
          // show a placeholder so the user can identify and remove the stale favorite.
          const isUnresolvable =
            (fav.sale_type !== "F" && !fullProperty.saleId && !fullProperty.originalSaleId) ||
            (fav.sale_type === "F" && !fullProperty.id && !fullProperty.prop_int_id);

          if (isUnresolvable) {
            return (
              <div
                key={fav.favorite_id || idx}
                className="d-flex flex-column justify-content-between"
                style={{
                  width: "21rem",
                  minHeight: "200px",
                  margin: "2rem",
                  padding: "1rem",
                  border: "1px solid #dee2e6",
                  borderRadius: "8px",
                }}
              >
                <p className="text-muted mb-1 text-center">This property is no longer available.</p>
                <p className="text-muted mb-3 text-center" style={{ fontSize: "12px" }}>
                  {fav.county}, {fav.state}
                  {fav.sale_date ? ` — ${fav.sale_date.split("T")[0]}` : ""}
                </p>
                <Button variant="outline-danger" size="sm" onClick={() => onRemoveFavorite(fav.favorite_id)}>
                  Remove
                </Button>
              </div>
            );
          }

          return (
            <PropertiesCard
              key={fav.favorite_id || idx}
              property={fullProperty}
              favoriteId={fav.favorite_id}
              originalSaleId={fullProperty.originalSaleId}
              handleDetailsClick={onDetailsClick}
              handleRegisterOrUnregisterClick={() => {
                if (saleId === null || saleId === undefined || isNaN(Number(saleId))) {
                  triggerToast("Invalid sale ID for this favorite.");
                  return;
                }
                onRegisterClick({ saleId, auction: fullProperty });
              }}
              isPropertyFavorite={isPropertyFavorite}
              isRegistered={isReg}
              getFavoriteButtonLabel={() => (isPropertyFavorite(fullProperty) ? "Unfavorite" : "Favorite")}
              getRegisterButtonLabel={() => (isReg ? "Unregister" : "Register")}
              removeFavoriteFunc={() => {
                if (!fav.favorite_id) {
                  triggerToast("No favorite_id found.");
                  return;
                }
                onRemoveFavorite(fav.favorite_id);
              }}
              addFavoriteFunc={() => onAddFavorite(fullProperty)}
              userRegistrations={registrations}
              propIndex={idx}
            />
          );
        })}
      </div>

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
  );
};

export default FavoritesList;
